import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from '../../../utils/store';
import { useEffect, useRef, useState } from 'react';
import Constants from '../../../utils/constants';
import HttpService from '../../../services/HttpService';
import FormFieldData from '../../../Models/FormFieldData';
import { Stack } from '@mui/material';
import CommonForm from '../../../components/form/CommonForm';
import AlertMessageComponent from '../../../components/AlertMessageComponent';
import { getCurrentDate } from '../../../utils/storageUtil';
import { useAlert } from '../../../components/alertContext';

const TendersForm: React.FC = () => {
  interface tenderFormFields {
    [k: string]: { required: boolean; value: any };
  }
  interface TenderType {
    id: string;
    label: string;
  }
  const isInitialLoad = useRef(true);
  const { getAdmin } = useStore();
  const { currentId } = useParams<{ currentId: any }>();
  const navigate = useNavigate();

  const [uploadFile, setUploadFile] = useState<any[]>([]);
  const { alert, setAlert, handleCloseAlert } = useAlert();
  const [loading, setLoading] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<any>(null);
  const [currentFilesMedia, setCurrentFilesMedia] = useState<any[]>([]);
  const [tenderForm, setTenderForm] = useState<tenderFormFields>({
    tenderType: { required: true, value: '' },
    docName: { required: true, value: '' },
    expiryDate: { required: true, value: getCurrentDate() },
    id: { required: true, value: '' },
    notes: { required: false, value: '' },
    uploadFile: { required: true, value: [] },
    fileName: { required: true, value: '' },
  });
  const [tenderTypes, setTenderTypes] = useState<TenderType[]>([]);

  useEffect(() => {
    const fetchTenderTypes = async () => {
      try {
        const response = await HttpService.get(
          Constants.apiURL + Constants.refBundleURL + '/fetchTenderTypes',
          {
            headers: {
              Authorization: 'Bearer ' + getAdmin(),
            },
          },
          null
        );
        if (response.data) {
          const fetchedTenderTypes = response.data.map((item: any) => ({
            id: item.id,
            label: item.name,
          }));
          console.log(fetchedTenderTypes);
          setTenderTypes(fetchedTenderTypes);
        }
        console.log('GET Response:', response);
      } catch (error: any) {
        setTenderTypes([]);
        console.error(`GET Error:`, error);
        setAlert({
          type: 'error',
          message: error.message,
          open: true,
        });
      }
    };

    if (tenderTypes.length === 0 && isInitialLoad.current) {
      fetchTenderTypes();
    }

    isInitialLoad.current = false;
  }, []);

  const handleRowClick = async (record: any) => {
    setCurrentRecord(record);

    setTenderForm({
      expiryDate: { required: true, value: record.expiryDate },
      docName: { required: true, value: record.docName },
      uploadFile: { required: true, value: record.documents },
      id: { required: false, value: record.id },
      notes: { required: false, value: record.notes },
      tenderType: { required: true, value: record.tenderType },
      fileName: { required: true, value: record.fileName },
    });
    setCurrentFilesMedia(record.documents);
  };

  const fetchSingleRecord = async (id: any) => {
    try {
      const response = await HttpService.get(
        Constants.apiURL + Constants.tendersURL + '/' + id,
        { headers: { Authorization: 'Bearer ' + getAdmin() } },
        null
      );
      if (response.success) {
        handleRowClick(response.data);
      }

      console.log('GET Response:', response);
    } catch (error: any) {
      console.error(`GET Error:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };

  useEffect(() => {
    if (currentId) {
      if (currentId !== 'Add') {
        fetchSingleRecord(currentId);
      }
    } else {
      setCurrentRecord(null);
    }
  }, [currentId]);

  // TODO: Remove function?
  const handlePresignedURL = async (
    file: any,
    relationType: any,
    relationId: any
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        if (file) {
          const fileName = file.name;
          const response = await HttpService.post(
            `${Constants.apiURL}${Constants.documentMediaURL}/uploadFileSignedURL`,
            { relationType, relationId, fileName },
            {
              headers: {
                Authorization: 'Bearer ' + getAdmin(),
              },
            }
          );
          if (response.success) {
            const fields = response.data.fields;
            fields['file'] = file;
            const url = response.data.url;
            const responseData = await handleFileUpload(fields, url);
            console.log(responseData);
            if (responseData) {
              await handleMediaResponse({
                relationId,
                relationType,
                key: response.data.filePath,
                fileName: response.data.fileName,
                filePath: response.data.filePath,
              });
              resolve(true);
            }
          } else {
            resolve(false);
          }
          console.log('GET Response:', response);
        }
      } catch (error: any) {
        resolve(false);
        console.error(`GET Error:`, error);
        setAlert({
          type: 'error',
          message: error.message,
          open: true,
        });
      }
    });
  };
  const handleFileUpload = async (fields: any, url: any) => {
    return new Promise(async (resolve, reject) => {
      try {
        if (fields) {
          const response = await HttpService.s3Upload(url, fields, {
            headers: { enctype: 'multipart/form-data' },
          });
          resolve(true);
          console.log('GET Response:', response);
        }
      } catch (error: any) {
        resolve(false);
        console.error(`GET Error:`, error);
        setAlert({
          type: 'error',
          message: error.message,
          open: true,
        });
      }
    });
  };

  const clearForm = () => {
    setTenderForm({
      tenderType: { required: true, value: 'Insurances' },
      docName: { required: true, value: '' },
      expiryDate: { required: true, value: getCurrentDate() },
      id: { required: false, value: '' },
      notes: { required: false, value: '' },
      uploadFile: { required: true, value: [] },
      fileName: { required: true, value: '' },
    });
    setCurrentRecord(null);
    setAlert({
      type: 'error',
      message: '',
      open: false,
    });
    navigate('/data-management/tender');
  };
  const handleMediaResponse = async (formData: any) => {
    try {
      return await HttpService.post(
        Constants.apiURL + Constants.documentMediaURL,
        formData,
        { headers: { Authorization: 'Bearer ' + getAdmin() } }
      );
    } catch (error: any) {
      console.error(`POST Error:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
      return null;
    }
  };
  const CreateOrUpdate = async (formData: any) => {
    try {
      let response: any;
      if (formData['id']) {
        response = await HttpService.put(
          Constants.apiURL + Constants.tendersURL,
          formData,
          { headers: { Authorization: 'Bearer ' + getAdmin() } }
        );
      } else {
        response = await HttpService.post(
          Constants.apiURL + Constants.tendersURL,
          formData,
          { headers: { Authorization: 'Bearer ' + getAdmin() } }
        );
      }

      if (response.success) {
        for (let k = 0; k < uploadFile.length; k++) {
          await HttpService.handlePresignedURL(
            uploadFile[k],
            'tender',
            response.data.id,
            getAdmin()
          );
        }
        clearForm();
      }

      console.log('GET Response:', response);
    } catch (error: any) {
      console.error(`GET Error:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };

  const handleCurrentFilesRemove = (fileData: any) => {
    const updatedFiles = currentFilesMedia.filter(
      (file: any) => file !== fileData
    );
    setCurrentFilesMedia(updatedFiles);
  };

  const handleSave = async () => {
    setLoading(true);
    const formData: any = {
      expiryDate: tenderForm.expiryDate.value,
      docName: tenderForm.docName.value,
      notes: tenderForm.notes.value,
      tenderTypeId: tenderForm.id.value,
    };
    console.log(formData);
    if (currentRecord) {
      formData['id'] = currentRecord.id;
      formData['documents'] = currentFilesMedia;
    }
    await CreateOrUpdate(formData);
    setLoading(false);
  };

  const handleExpiryDate = (value: any) => {
    const expiryDateValue =
      value.year() + '-' + (value.month() + 1) + '-' + value.date();
    setTenderForm((prev: any) => {
      return {
        ...prev,
        expiryDate: {
          required: prev['expiryDate'].required,
          value: expiryDateValue,
        },
      };
    });
  };

  const handleRemove = (file: any) => {
    setUploadFile([]);
  };

  const handleFileChange = (file: any) => {
    console.log(`filesData`);
    console.log(file);
    console.log(`filesData`);
    setTenderForm((prev: any) => {
      return {
        ...prev,
        uploadFile: { required: prev['uploadFile'].required, value: file },
      };
    });

    setUploadFile(file);
  };
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLFormElement
    >
  ) => {
    const { name, value, type } = e.target;
    setTenderForm((prev: any) => {
      // Check if the input is a checkbox
      if (type === 'checkbox') {
        const target: any = e.target;
        const currentValues = prev[name] || []; // Get the current array or initialize it
        if (target.checked && currentValues.includes(value) === false) {
          return {
            ...prev,
            [name]: {
              required: prev[name].required,
              value: [...currentValues, value],
            },
          };
        } else {
          return {
            ...prev,
            [name]: {
              required: prev[name].required,
              value: currentValues.filter((id: any) => id !== value),
            },
          };
        }
      } else if (e.type === 'click') {
        console.log([
          value,
          tenderTypes.filter((item) => item.id === value)[0].label,
        ]);
        return {
          ...prev,
          id: { required: prev.id.required, value },
          [name]: {
            required: prev[name].required,
            value: tenderTypes.filter((item) => item.id === value)[0].label,
          },
        };
      } else {
        console.log(e);
        // Handle other input types
        return {
          ...prev,
          [name]: { required: prev[name].required, value: value },
        };
      }
    });
  };

  const formFields: FormFieldData[] = [
    {
      name: 'tenderType',
      label: 'Type',
      value: tenderForm.id.value,
      onChange: handleInputChange,
      isRequired: tenderForm.id.required,
      size: 12,
      type: 'selectField',
      validationRules: [(value) => (!value ? 'Type is required' : '')],
      options: tenderTypes,
    },
    {
      name: 'docName',
      label: 'Document Name',
      value: tenderForm.docName.value,
      onChange: handleInputChange,
      isRequired: tenderForm.docName.required,
      size: 6,
      type: 'textfield',
      validationRules: [(value) => (!value ? 'Document name is required' : '')],
    },
    {
      name: 'expiryDate',
      label: 'Expiry Date',
      value: tenderForm.expiryDate.value,
      onChange: handleExpiryDate,
      isRequired: tenderForm.expiryDate.required,
      keyname: 'expiryDateTender',
      size: 6,
      type: 'datepicker',
      validationRules: [(value) => (!value ? 'Expiry Date is required' : '')],
    },
    {
      name: 'notes',
      label: 'Notes',
      value: tenderForm.notes.value,
      onChange: handleInputChange,
      isRequired: tenderForm.notes.required,
      size: 12,
      minRows: 7,
      type: 'textareaField',
      validationRules: [],
    },
    {
      name: 'uploadFile',
      label: 'Attachment',
      isRequired: tenderForm.uploadFile.required,
      multiple: true,
      value: tenderForm.uploadFile.value,
      existingFiles: currentFilesMedia,
      onChange: (file: any) => {
        handleFileChange(file);
      },
      onRemoveFile: (file: any) => {
        handleRemove(file);
      },
      onRemoveExistingFile: (file: any) => {
        handleCurrentFilesRemove(file);
      },
      onOpenFile: (id) => {
        HttpService.handleDownloadFile(id, getAdmin());
      },
      size: 12,
      type: 'file',
      validationRules: [
        (value) =>
          value.length === 0 && currentFilesMedia.length === 0
            ? 'Tender Document is required'
            : '',
      ],
    },
  ];
  return (
    <Stack>
      <CommonForm
        fields={formFields}
        onCancel={() => {}}
        formReset={clearForm}
        formTitle={currentId !== 'Add' ? 'Update Tender' : 'Add New Tender'}
        isLoading={loading}
        isUpdate={currentId !== 'Add' ? true : false}
        onSave={handleSave}
        actionButtonPosition="bottom"
      />
      <AlertMessageComponent
        type={alert.type}
        open={alert.open}
        handleClose={handleCloseAlert}
        message={alert.message}
      />
    </Stack>
  );
};

export default TendersForm;
